import React from 'react'

import { isMobile } from 'utils'
import { Field } from 'common/fields/entities/field'
import { PreloadImagesArray } from 'components/RichTextEditor/processor/constants'
import { InputElement } from 'components/RichTextEditor/types'
import { IMinimalChangeEventHandler } from 'constants/constants'

import MCTextEditor, { EditorTheme, IMCTextEditorRef } from './MCTextEditor'
import { Tools } from './toolbar/toolbarInterfaces'
import { AllowedValues } from './types'

const EXTRA_INPUT_PADDING_BOTTOM = 20

export interface IMCTextEditorLegacyProps {
  className?: string
  'data-test-id'?: string
  'data-onboarding-id'?: string
  disabled?: boolean
  inputPadding?: number
  inverse?: boolean
  isValid?: boolean
  maxRows?: number
  minRows?: number
  multiline?: boolean
  name?: string
  noDelay?: boolean
  focusOnMount?: boolean
  noLineBreaks?: boolean
  noWrap?: boolean
  onBlur?: React.FocusEventHandler<InputElement>
  onChange?: IMinimalChangeEventHandler
  onFocus?: React.FocusEventHandler<InputElement>
  onKeyDown?: React.KeyboardEventHandler<InputElement>
  onPasteImage?: (imageFiles: File[]) => void
  placeholder?: string
  readonly?: boolean
  theme?: EditorTheme
  useContentEditable?: boolean
  value?: AllowedValues
  withDefaultStyles?: boolean
  withEmoji?: boolean
  withHyperlinks?: boolean
  withSmartLinks?: boolean
  withVariables?: boolean
  withAiTextImprover?: boolean
  // BUILDER
  builderId?: string
  blockId?: string
  // VARIABLES
  getFields?: () => Field[]
  fallbackMode?: 'disabled' | 'number'
  json?: boolean
  preview?: boolean
  // TOOLBAR
  toolbarMode?: 'default' | 'disabled' | 'richtext'
  limit?: number
  countVariableLength?: boolean
  countEncodedSymbols?: boolean
  includeInstagramCategoryToEmojiPicker?: boolean
  preloadImages?: PreloadImagesArray
  preloadImagesClassName?: string
  withExtraToolbarSpacing?: boolean
  imagesWidgetDisabled?: boolean
}

const MCTextEditorWithLegacyAPI: React.ForwardRefRenderFunction<
  IMCTextEditorRef,
  IMCTextEditorLegacyProps
> = (props, ref) => {
  const {
    className,
    disabled,
    inputPadding,
    inverse,
    isValid = true,
    focusOnMount = true,
    maxRows,
    minRows,
    multiline,
    name,
    noDelay,
    noLineBreaks,
    noWrap,
    onBlur,
    onChange,
    onFocus,
    onKeyDown,
    onPasteImage,
    placeholder,
    readonly,
    theme,
    useContentEditable,
    value,
    withDefaultStyles,
    withEmoji = !isMobile,
    withHyperlinks = false,
    withSmartLinks = false,
    withVariables = true,
    withAiTextImprover = false,
    preloadImages = [],
    preloadImagesClassName,
    // BUILDER
    builderId,
    blockId,
    // VARIABLES
    getFields,
    json,
    fallbackMode,
    preview,
    // TOOLBAR
    toolbarMode = 'default',
    limit,
    countVariableLength,
    countEncodedSymbols,
    includeInstagramCategoryToEmojiPicker,
    withExtraToolbarSpacing,
    imagesWidgetDisabled,
  } = props

  const toolbarConfig = {
    mode: toolbarMode,
    limit,
    countVariableLength,
    countEncodedSymbols,
    includeInstagramCategoryToEmojiPicker,
    withExtraToolbarSpacing,
    tools: getTools({
      withVariables,
      withEmoji,
      withSmartLinks,
      withAiTextImprover,
    }),
  }

  return (
    <MCTextEditor
      ref={ref}
      className={className}
      data-test-id={props['data-test-id']}
      data-onboarding-id={props['data-onboarding-id']}
      disabled={disabled}
      inputPadding={inputPadding}
      inputPaddingBottom={withExtraToolbarSpacing ? EXTRA_INPUT_PADDING_BOTTOM : inputPadding}
      inverse={inverse}
      isValid={isValid}
      maxRows={maxRows}
      minRows={minRows}
      multiline={multiline}
      name={name}
      noDelay={noDelay}
      noLineBreaks={noLineBreaks}
      noWrap={noWrap}
      onBlur={onBlur}
      onChange={onChange}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      onPasteImage={onPasteImage}
      placeholder={placeholder}
      readonly={readonly}
      theme={theme}
      value={value}
      focusOnMount={focusOnMount}
      toolbarConfig={toolbarConfig}
      useContentEditable={useContentEditable}
      variablesConfig={{ getFields, json, fallbackMode, preview }}
      withDefaultStyles={withDefaultStyles}
      withSmartLinks={withSmartLinks}
      withVariables={withVariables}
      preloadImages={preloadImages}
      preloadImagesClassName={preloadImagesClassName}
      withHyperlinks={withHyperlinks}
      builderId={builderId}
      blockId={blockId}
      imagesWidgetDisabled={imagesWidgetDisabled}
    />
  )
}

export default React.forwardRef<IMCTextEditorRef, IMCTextEditorLegacyProps>(
  MCTextEditorWithLegacyAPI,
)

interface GetToolsProps {
  withVariables: boolean
  withEmoji: boolean
  withSmartLinks: boolean
  withAiTextImprover: boolean
}

const getTools = ({
  withVariables,
  withEmoji,
  withSmartLinks,
  withAiTextImprover,
}: GetToolsProps) => {
  const result: Tools[] = []
  withVariables && result.unshift(Tools.vars)
  withEmoji && result.unshift(Tools.emoji)
  withSmartLinks && result.unshift(Tools.smartLinks)
  withAiTextImprover && result.unshift(Tools.aiContentGenerator)

  return result
}
